import { useQuery, useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type {
  UserDto,
  InviteInfo,
  ClustersResponse,
  ClusterDetail,
  ArticleContent,
  FeedDto,
  SubscribeBody,
  DiscoveredFeed,
  Settings,
  SettingsResponse,
  TranslationDto,
  MuteKind,
  MuteDto,
  StarterPack,
  HealthResponse,
  WeeklyStats,
  TopicsResponse,
  AiCategory,
  RegistrationModeResponse,
  AdminOverview,
  AdminTimeseries,
  AdminErrors,
  AdminUsers,
  AdminUserDetail,
  AdminRange,
  AdminUsersSort,
  AiUsageKind,
} from '@b4b/shared';
import { api, ApiError } from './client.js';

// --- auth ---

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<{ user: UserDto }>('/api/auth/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** Public: drives the landing/login CTA (sign-up hidden when closed, invite field when invite_code). */
export function useRegistrationMode() {
  return useQuery({
    queryKey: ['registration-mode'],
    queryFn: () => api.get<RegistrationModeResponse>('/api/auth/registration-mode'),
    staleTime: Infinity,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/api/auth/logout'),
    onSuccess: () => {
      qc.setQueryData(['me'], null);
      qc.clear();
    },
  });
}

// --- clusters ---

export function useClusters(
  filter: 'unread' | 'all' | 'saved',
  category?: string,
  tag?: string,
  topic?: AiCategory,
  options: { enabled?: boolean } = {},
) {
  const params = new URLSearchParams({ filter });
  if (category) params.set('category', category);
  if (tag) params.set('tag', tag);
  if (topic) params.set('topic', topic);
  return useQuery({
    queryKey: ['clusters', filter, category ?? null, tag ?? null, topic ?? null],
    queryFn: () => api.get<ClustersResponse>(`/api/clusters?${params.toString()}`),
    enabled: options.enabled ?? true,
  });
}

export interface FeedQueryParams {
  filter: 'unread' | 'all';
  category?: string | undefined;
  tag?: string | undefined;
  topic?: AiCategory | undefined;
}

/** Main feed: cursor-paginated pages of clusters, flattened. Key shares the
 * ['clusters'] prefix so every existing invalidation reaches it. */
export function useClustersFeed(params: FeedQueryParams, options: { enabled?: boolean } = {}) {
  const query = useInfiniteQuery({
    queryKey: ['clusters', 'feed', params.filter, params.category ?? null, params.tag ?? null, params.topic ?? null],
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams({ filter: params.filter });
      if (params.category) search.set('category', params.category);
      if (params.tag) search.set('tag', params.tag);
      if (params.topic) search.set('topic', params.topic);
      if (pageParam) search.set('cursor', pageParam);
      return api.get<ClustersResponse>(`/api/clusters?${search.toString()}`);
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: options.enabled ?? true,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  return { ...query, items };
}

/** Topics layout: sections per AI category (see /api/clusters/topics). */
export function useTopics(filter: 'unread' | 'all', category?: string, options: { enabled?: boolean } = {}) {
  const params = new URLSearchParams({ filter });
  if (category) params.set('category', category);
  return useQuery({
    queryKey: ['topics', filter, category ?? null],
    queryFn: () => api.get<TopicsResponse>(`/api/clusters/topics?${params.toString()}`),
    enabled: options.enabled ?? true,
  });
}

export function useClusterDetail(id: string) {
  return useQuery({
    queryKey: ['cluster', id],
    queryFn: () => api.get<ClusterDetail>(`/api/clusters/${id}`),
  });
}

export function useArticleContent(clusterId: string, articleId: string | null) {
  return useQuery({
    queryKey: ['article', clusterId, articleId],
    queryFn: () => api.get<ArticleContent>(`/api/clusters/${clusterId}/article/${articleId}`),
    enabled: articleId !== null,
  });
}

export function useSetRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) =>
      api.post<void>(`/api/clusters/${id}/read`, { read }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clusters'] });
      void qc.invalidateQueries({ queryKey: ['topics'] });
      void qc.invalidateQueries({ queryKey: ['cluster'] });
    },
  });
}

export function useSetSaved() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, saved }: { id: string; saved: boolean }) =>
      api.post<void>(`/api/clusters/${id}/saved`, { saved }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clusters'] });
      void qc.invalidateQueries({ queryKey: ['topics'] });
      void qc.invalidateQueries({ queryKey: ['cluster'] });
    },
  });
}

/** Mark read after the story was scrolled past — updates caches in place
 * (no invalidation) so the reel doesn't jump while the user is scrolling. */
export function useMarkSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/api/clusters/${id}/read`, { read: true }),
    onSuccess: (_data, id) => {
      const markPage = (data: ClustersResponse): ClustersResponse => {
        let decremented = false;
        const items = data.items.map((item) => {
          if (item.id === id && !item.read) {
            decremented = true;
            return { ...item, read: true };
          }
          return item;
        });
        return {
          ...data,
          items,
          unreadCount: decremented ? Math.max(0, data.unreadCount - 1) : data.unreadCount,
        };
      };
      // the cache holds plain pages (drawer/saved/search) and infinite pages (main feed)
      qc.setQueriesData<ClustersResponse | InfiniteData<ClustersResponse>>({ queryKey: ['clusters'] }, (data) => {
        if (!data) return data;
        if ('pages' in data) return { ...data, pages: data.pages.map(markPage) };
        return markPage(data);
      });
      void qc.invalidateQueries({ queryKey: ['topics'] });
    },
  });
}

export function useReadAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (before: string) => api.post<void>('/api/clusters/read-all', { before }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clusters'] });
      void qc.invalidateQueries({ queryKey: ['topics'] });
    },
  });
}

// --- feeds ---

export function useFeeds() {
  return useQuery({ queryKey: ['feeds'], queryFn: () => api.get<FeedDto[]>('/api/feeds') });
}

export function useDiscover() {
  return useMutation({
    mutationFn: (url: string) => api.post<DiscoveredFeed[]>('/api/feeds/discover', { url }),
  });
}

export function useSubscribe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubscribeBody) => api.post<FeedDto>('/api/feeds', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feeds'] });
      void qc.invalidateQueries({ queryKey: ['clusters'] });
      void qc.invalidateQueries({ queryKey: ['topics'] });
    },
  });
}

export function useUnsubscribe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (feedId: string) => api.delete<void>(`/api/feeds/${feedId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feeds'] });
      void qc.invalidateQueries({ queryKey: ['clusters'] });
      void qc.invalidateQueries({ queryKey: ['topics'] });
    },
  });
}

export function useUpdateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ feedId, category }: { feedId: string; category: string | null }) =>
      api.patch<void>(`/api/feeds/${feedId}`, { category }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['feeds'] }),
  });
}

// --- settings ---

export function useSettings() {
  return useQuery({ queryKey: ['settings'], queryFn: () => api.get<SettingsResponse>('/api/settings') });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<Settings>) => api.patch<SettingsResponse>('/api/settings', patch),
    // optimistic: layout/theme switches must feel instant, the server confirms afterwards
    onMutate: (patch) => {
      const previous = qc.getQueryData<SettingsResponse>(['settings']);
      if (previous) qc.setQueryData<SettingsResponse>(['settings'], { ...previous, ...patch });
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (context?.previous) qc.setQueryData(['settings'], context.previous);
    },
    onSuccess: (data) => qc.setQueryData(['settings'], data),
  });
}

// --- mutes ---

export function useMutes() {
  return useQuery({ queryKey: ['mutes'], queryFn: () => api.get<MuteDto[]>('/api/mutes') });
}

export function useAddMute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { keyword: string; kind?: MuteKind }) =>
      api.post<MuteDto>('/api/mutes', { keyword: input.keyword, kind: input.kind ?? 'word' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mutes'] });
      void qc.invalidateQueries({ queryKey: ['clusters'] });
      void qc.invalidateQueries({ queryKey: ['topics'] });
    },
  });
}

export function useDeleteMute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/mutes/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mutes'] });
      void qc.invalidateQueries({ queryKey: ['clusters'] });
      void qc.invalidateQueries({ queryKey: ['topics'] });
    },
  });
}

// --- translation (premium) ---

/** 200 = the translation, 202 = the server queued the full text (poll again). */
export type TranslateResult = TranslationDto | { status: 'pending' };

export function useTranslateArticle() {
  return useMutation({
    mutationFn: (articleId: string) => api.post<TranslateResult>(`/api/articles/${articleId}/translate`),
  });
}

// --- misc ---

/** Subscribe to every feed of a starter pack (category = pack name in the user's language). */
export function useSubscribePack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (packId: string) => api.post<{ subscribed: number }>(`/api/starter-packs/${packId}/subscribe`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['feeds'] });
      void qc.invalidateQueries({ queryKey: ['clusters'] });
      void qc.invalidateQueries({ queryKey: ['topics'] });
    },
  });
}

export function useStarterPacks() {
  return useQuery({
    queryKey: ['starter-packs'],
    queryFn: () => api.get<StarterPack[]>('/api/starter-packs'),
    staleTime: Infinity,
  });
}

export function useHealth() {
  return useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<HealthResponse>('/api/health'),
    refetchInterval: 60_000,
  });
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get<ClustersResponse>(`/api/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length > 1,
  });
}

export function useWeeklyStats() {
  return useQuery({
    queryKey: ['weekly-stats'],
    queryFn: () => api.get<WeeklyStats>('/api/stats/weekly'),
  });
}

// --- invites ---

/** The signed-in user's personal invite code and how many of the allowed invites are used. */
export function useInvites() {
  return useQuery({ queryKey: ['invites'], queryFn: () => api.get<InviteInfo>('/api/invites'), staleTime: 60_000 });
}

// --- admin (ADMIN_EMAILS only; never persisted to the offline cache) ---

export function useAdminOverview(range: AdminRange) {
  return useQuery({
    queryKey: ['admin', 'overview', range],
    queryFn: () => api.get<AdminOverview>(`/api/admin/overview?range=${range}`),
    staleTime: 60_000,
  });
}

/** Shared query options for the AI usage timeseries, so a single kind (useAdminTimeseries)
 * and one-request-per-kind (AiTimeseriesChart, via useQueries) never duplicate the key/URL. */
export function adminTimeseriesQueryOptions(range: AdminRange, kind?: AiUsageKind) {
  const params = new URLSearchParams({ range });
  if (kind) params.set('kind', kind);
  return {
    queryKey: ['admin', 'timeseries', range, kind ?? 'all'] as const,
    queryFn: () => api.get<AdminTimeseries>(`/api/admin/ai/timeseries?${params}`),
    staleTime: 60_000,
  };
}

export function useAdminTimeseries(range: AdminRange, kind?: AiUsageKind) {
  return useQuery(adminTimeseriesQueryOptions(range, kind));
}

export function useAdminErrors(kind?: AiUsageKind, enabled = true) {
  const params = new URLSearchParams({ limit: '20' });
  if (kind) params.set('kind', kind);
  return useQuery({
    queryKey: ['admin', 'errors', kind ?? 'all'],
    queryFn: () => api.get<AdminErrors>(`/api/admin/ai/errors?${params}`),
    enabled,
  });
}

export function useAdminUsers(sort: AdminUsersSort) {
  return useQuery({
    queryKey: ['admin', 'users', sort],
    queryFn: () => api.get<AdminUsers>(`/api/admin/users?sort=${sort}&limit=200`),
    staleTime: 60_000,
  });
}

export function useAdminUser(id: string) {
  return useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => api.get<AdminUserDetail>(`/api/admin/users/${id}`),
  });
}
